// src/Dashboard/pages/user/Reglements/components/ReglementCard.jsx

import React from "react";
import {
  FaBell,
  FaCheckCircle,
  FaCoins,
  FaCreditCard,
  FaLock,
  FaFileInvoice,
} from "react-icons/fa";

import {
  FALLBACK_IMAGE,
  canPayInInstallments,
  canShowPaymentButtons,
  formatDisplayDate,
  formatEUR,
  getArticleMessage,
  getArticleName,
  getStatusMeta,
  canGenerateInvoice,
} from "../utils/reglementsUtils";

export default function ReglementCard({
  reglement,
  payingId,
  notifyingId,
  isAlreadyNotified,
  onPay,
  onInstallments,
  onNotify,
  onInvoice,
  maxPayments = 1,
}) {
  const statusMeta = getStatusMeta(reglement?.statut_reglement);
  const name = getArticleName(reglement);
  const message = getArticleMessage(reglement);

  const isPaymentDisabled = reglement?.autorisation_achat === false;
  const showPaymentButtons = canShowPaymentButtons(reglement);
  const showInstallments = canPayInInstallments(reglement, maxPayments);
  const alreadyNotified = isAlreadyNotified?.(reglement.id);
  const showInvoice = canGenerateInvoice(reglement);

  return (
    <article className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="h-1 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4">
        <div className="flex gap-4">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <img
              src={reglement.photoUrl || FALLBACK_IMAGE}
              alt={name}
              className="h-full w-full object-contain"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  {reglement.saison}
                </p>

                <h3 className="mt-1 line-clamp-2 text-base font-bold text-slate-900 dark:text-white">
                  {reglement.quantite} x {name}
                </h3>
              </div>

              <span
                className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-bold ring-1 ${statusMeta.badge}`}
              >
                {statusMeta.label}
              </span>
            </div>

            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Achat du {formatDisplayDate(reglement.date_achat)}
            </p>

            {message && (
              <p className="mt-2 line-clamp-2 text-sm italic text-slate-600 dark:text-slate-300">
                {message}
              </p>
            )}

            <p className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
              {formatEUR(reglement.prix_total)}
            </p>
          </div>
        </div>

        {showPaymentButtons && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
            {isPaymentDisabled && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-2xl bg-red-50 px-3 py-2 text-xs font-bold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
                <FaLock />
                Paiement désactivé par le bureau
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => onPay(reglement.id)}
                disabled={isPaymentDisabled || payingId === reglement.id}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaCreditCard />
                {payingId === reglement.id
                  ? "Redirection..."
                  : "Payer en ligne"}
              </button>

              {showInstallments && (
                <button
                  type="button"
                  onClick={() => onInstallments(reglement.id)}
                  disabled={isPaymentDisabled}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900"
                >
                  <FaCoins />
                  Payer en plusieurs fois
                </button>
              )}
            </div>
          </div>
        )}

        {reglement.statut_reglement === "Réglé" && (
          <div className="mt-4 flex flex-wrap gap-2">
            {showInvoice && (
              <button
                type="button"
                onClick={() => onInvoice?.(reglement)}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
              >
                <FaFileInvoice />
                Facture PDF
              </button>
            )}

            {alreadyNotified ? (
              <span className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                <FaCheckCircle />
                Admin prévenu
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onNotify(reglement)}
                disabled={notifyingId === reglement.id}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaBell />
                {notifyingId === reglement.id ? "Envoi..." : "Prévenir l’admin"}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}